import Pagination from '../../components/shared/pagination';
import React, { useEffect, useMemo, useState } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    getFilteredRowModel,
    flexRender,
} from '@tanstack/react-table';
import { statusColors } from '../../utils/status-color-utils';
import { RxCaretSort } from 'react-icons/rx';
import { RiArrowUpSLine } from 'react-icons/ri';
import Loader from '../../components/shared/loader';
import { formatUTCTimestampToLocal } from '../../utils/set-date-range-utils';
import { ImMap2 } from "react-icons/im";
import { getClockPositions } from '../../services/clock-service';
import PositionsMap from './position-map';
import Modal from '../../components/shared/modal';
import { APIProvider } from '@vis.gl/react-google-maps';

const ClockTable = ({
    data,
    setData,
    globalFilter,
    onGlobalFilterChange,
    fetchClocks,
    loadingData,
    accountId,
    spendors,
    attributes,
    vehicles,
    setSelectedRows, selectedRows,
}) => {
    const [sorting, setSorting] = useState([]);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isUpdateStatusModalOpen, setIsUpdateStatusModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [isMapModalOpen, setIsMapModalOpen] = useState(false);
    const [positionLoading, setPositionLoading] = useState(false);
    const [selectedPositions, setSelectedPositions] = useState([]);

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => {
                setSuccessMessage(null);
            }, 2000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const handleActionClick = (id) => {
        setOpenMenuId(openMenuId === id ? null : id);
    };

    const closeMenu = () => {
        setOpenMenuId(null);
    };

    const handleRowClick = (row, e) => {
        if (!e.target.closest('input[type="checkbox"]')) {
            setSelectedRow(row.original);
            setIsEditModalOpen(true);
        }
    };

    const handleConfirmAction = (action, row) => {
        setSelectedRow(row.original);
        if (action === 'Change Status') {
            setIsUpdateStatusModalOpen(true);
        } else {
            setConfirmAction(action);
            setIsConfirmModalOpen(true);
        }
        setErrorMessage(null);
        closeMenu();
    };

    useEffect(() => {
        const handleClickOutside = () => closeMenu();
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const getPositions = async (id) => {
        setErrorMessage(null);
        setPositionLoading(true);
        setIsMapModalOpen(true);

        try {
            const res = await getClockPositions(accountId, id);

            if (res?.data?.data && res.data.data.length > 0) {
                setSelectedPositions(res.data.data);

            } else {
                setErrorMessage("No Tracking location available for this record");
            }
        } catch (error) {
            console.error("Error fetching positions:", error);
            setErrorMessage(
                error?.response?.data?.message || "Failed to fetch positions. Please try again."
            );
        } finally {
            setPositionLoading(false);
        }
    };


    const columns = useMemo(
        () => [
            {
                id: 'select',
                header: ({ table }) => (
                    <div className="flex justify-center w-full">
                        <input
                            type="checkbox"
                            checked={table.getIsAllRowsSelected()}
                            onChange={(e) => {
                                e.stopPropagation();
                                table.getToggleAllRowsSelectedHandler()(e);
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                cell: ({ row }) => (
                    <div className="flex justify-center w-full" onClick={(e) => e.stopPropagation()}>
                        <input
                            type="checkbox"
                            checked={row.getIsSelected()}
                            onChange={(e) => {
                                e.stopPropagation();
                                row.getToggleSelectedHandler()(e);
                            }}
                            className="w-4 h-4"
                        />
                    </div>
                ),
                enableSorting: false,
                size: 50,
            },
            {
                accessorKey: 'clockId',
                header: <div className='whitespace-nowrap'>Clock Id</div>,
                enableSorting: true,
            },
            {
                accessorKey: 'user.name',
                header: <div className='whitespace-nowrap'>User</div>,
                enableSorting: true,
            },
            {
                accessorKey: 'clockIn',
                header: <div className='whitespace-nowrap'>Clock In</div>,
                enableSorting: true,
                cell: info => formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a'),
            },

            {
                accessorKey: 'clockInLocation',
                header: <div className='whitespace-nowrap'>Clock In Location</div>,
                enableSorting: true,
                cell: info => {
                    const location = info.getValue();
                    if (!location) return 'N/A';
                    const [lat, lng] = location.split(',');
                    const formattedLat = parseFloat(lat).toFixed(3);
                    const formattedLng = parseFloat(lng).toFixed(3);
                    return `${formattedLat}, ${formattedLng}`;
                },
            },

            {
                accessorKey: 'clockOut',
                header: <div className='whitespace-nowrap'>Clock Out</div>,
                enableSorting: true,
                cell: info => formatUTCTimestampToLocal(info.getValue(), 'yyyy-MM-dd hh:mm a'),
            },
            {
                accessorKey: 'clockOutLocation',
                header: <div className='whitespace-nowrap'>Clock Out Location</div>,
                enableSorting: true,
                cell: info => {
                    const location = info.getValue();
                    if (!location) return 'N/A';
                    const [lat, lng] = location.split(',');
                    const formattedLat = parseFloat(lat).toFixed(3);
                    const formattedLng = parseFloat(lng).toFixed(3);
                    return `${formattedLat}, ${formattedLng}`;
                },
            },

            {
                accessorKey: 'duration',
                header: 'Duration',
                enableSorting: true,
            },

            {
                accessorKey: 'status',
                header: 'Status',
                enableSorting: true,
                cell: info => {
                    const status = info.getValue().toLowerCase();
                    const color = statusColors[status] || statusColors.default;
                    return (
                        <div className="flex justify-start">
                            <span
                                className="py-0.5 rounded-md text-sm font-medium capitalize text-center whitespace-nowrap"
                                style={{
                                    backgroundColor: color,
                                    color: 'black',
                                    border: `1px solid ${color}`,
                                    width: '100px',
                                    display: 'inline-block',
                                    textAlign: 'center'
                                }}
                            >
                                {status}
                            </span>
                        </div>
                    );
                },
            },
            {
                accessorKey: 'positions',
                header: <div className='whitespace-nowrap text-center w-full'>Positions</div>,
                enableSorting: false,
                cell: ({ row }) => {
                    return (
                        <div className="relative flex justify-center w-full">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    getPositions(row.original.clockId);
                                }}
                                className="p-1 rounded-full hover:bg-gray-200"
                            >
                                <ImMap2 className="text-gray-800 text-lg" />
                            </button>
                        </div>
                    );
                },
            },
        ],
        [openMenuId]
    );


    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
            globalFilter,
            rowSelection: selectedRows,
        },
        onSortingChange: setSorting,
        onGlobalFilterChange,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        manualPagination: true,
        enableSortingRemoval: false,
        onRowSelectionChange: setSelectedRows,
        globalFilterFn: (row, columnId, filterValue) => {
            const searchLower = filterValue.toLowerCase();
            const rowValue = String(row.getValue(columnId)).toLowerCase();

            if (columnId === 'clockin' || columnId === 'clockout') {
                return formatUTCTimestampToLocal(rowValue, 'yyyy-MM-dd hh:mm a').toLowerCase().includes(searchLower);
            } else if (columnId.includes('.')) {
                const [parent, child] = columnId.split('.');
                const value = row.original[parent]?.[child] || '';
                return String(value).toLowerCase().includes(searchLower);
            }

            return rowValue.includes(searchLower);
        },
    });


    return (
        <div className='w-full'>
            {successMessage && (
                <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-md text-sm text-center">
                    {successMessage}
                </div>
            )}

            <div className="overflow-x-auto max-w-full pb-16">
                {loadingData ? (
                    <Loader />
                ) : (
                    <table className="min-w-full text-sm border border-gray-200">
                        <colgroup>
                            <col style={{ width: '50px' }} />
                            <col style={{ width: '50px' }} />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col />
                            <col style={{ width: '50px' }} />
                        </colgroup>
                        <thead>
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id} className="bg-gray-100 capitalize text-left">
                                    {headerGroup.headers.map(header => (
                                        <th
                                            key={header.id}
                                            className="px-2 py-1.5 border-b capitalize"
                                            onClick={header.column.getToggleSortingHandler()}
                                            style={{
                                                cursor: header.column.getCanSort() ? 'pointer' : 'default',
                                                width: header.column.getSize(),
                                            }}
                                        >
                                            <div className="flex items-center gap-1">
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                                {header.column.getCanSort() && (
                                                    <span className="text-black">
                                                        {{
                                                            asc: <RiArrowUpSLine className="text-base mt-0.5" />,
                                                            desc: <RiArrowUpSLine className="text-base rotate-180" />,
                                                        }[header.column.getIsSorted()] ?? (
                                                                <RxCaretSort className="text-lg" />
                                                            )}
                                                    </span>
                                                )}
                                            </div>
                                        </th>
                                    ))}
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows
                                .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                                .map(row => (
                                    <tr
                                        key={row.id}
                                        className="hover:bg-gray-50 cursor-pointer"
                                        onClick={(e) => handleRowClick(row, e)}
                                    >
                                        {row.getVisibleCells().map(cell => (
                                            <td
                                                key={cell.id}
                                                className="px-2 py-1 border-b whitespace-nowrap"
                                                style={{ width: cell.column.getSize() }}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                )}
            </div>

            <Pagination
                currentPage={currentPage}
                totalItems={table.getFilteredRowModel().rows.length}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={(theCount) => {
                    setItemsPerPage(theCount);
                    setCurrentPage(1);
                }}
            />

            <Modal
                isOpen={isMapModalOpen}
                onClose={() => setIsMapModalOpen(false)}
            >
                <APIProvider apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
                    <PositionsMap
                        positions={selectedPositions}
                        loading={positionLoading}
                        error={errorMessage}
                        onClose={() => setIsMapModalOpen(false)}
                        attributes={attributes}
                    />
                </APIProvider>
            </Modal>

        </div>
    );
};

export default ClockTable;